import { FiHash } from 'react-icons/fi';

export const countableUncountableData = {
  courseId: 'countable-uncountable',
  courseType: 'grammar',
  title: 'Countable & Uncountable Nouns',
  icon: FiHash,
  overview: {
    impact: 'Determines article, quantifier, and verb agreement choices',
    focus: 'Identifying countable vs uncountable nouns and using correct determiners',
    uses: 'Writing Tasks, Speaking accuracy',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'What Are Countable and Uncountable Nouns?',
      content: `<p><strong>Countable</strong> nouns can be counted: one book, two books. They have singular and plural forms.</p><p><strong>Uncountable</strong> nouns cannot be counted individually: water, information, advice. They have NO plural form.</p>`,
      examples: [
        {
          label: 'Countable',
          text: 'a chair, two chairs, many chairs'
        },
        {
          label: 'Uncountable',
          text: 'furniture (NOT furnitures), information (NOT informations)'
        }
      ]
    },
    {
      title: 'Common Uncountable Nouns in IELTS',
      content: `<p>These words are ALWAYS uncountable in English (common IELTS mistakes):</p>`,
      points: [
        'information (NOT informations)',
        'advice (NOT advices)',
        'research (NOT researches)',
        'equipment (NOT equipments)',
        'accommodation (NOT accommodations in British English)',
        'traffic, weather, news, knowledge, progress, evidence'
      ]
    },
    {
      title: 'Quantifiers',
      content: `<p>Different quantifiers work with countable and uncountable nouns:</p>`,
      table: {
        headers: [
          'Countable Only',
          'Uncountable Only',
          'Both'
        ],
        rows: [
          [
            'many',
            'much',
            'some/any'
          ],
          [
            'a few',
            'a little',
            'a lot of'
          ],
          [
            'several',
            'a great deal of',
            'plenty of'
          ],
          [
            'a number of',
            'an amount of',
            'enough'
          ]
        ]
      }
    }
  ],
  exercises: [
    {
      title: 'Countable or Uncountable?',
      type: 'select',
      instructions: 'Classify each noun.',
      questions: [
        {
          text: 'information',
          options: [
            'Uncountable',
            'Countable'
          ],
          answer: 'Uncountable',
          explanation: '\'Information\' has no plural form. Say \'a piece of information\'.'
        },
        {
          text: 'suggestion',
          options: [
            'Countable',
            'Uncountable'
          ],
          answer: 'Countable',
          explanation: '\'A suggestion\', \'two suggestions\' — it can be counted.'
        },
        {
          text: 'furniture',
          options: [
            'Uncountable',
            'Countable'
          ],
          answer: 'Uncountable',
          explanation: 'Say \'a piece of furniture\', never \'furnitures\'.'
        },
        {
          text: 'research',
          options: [
            'Uncountable',
            'Countable'
          ],
          answer: 'Uncountable',
          explanation: '\'Research\' is uncountable. Say \'a piece/body of research\'.'
        }
      ]
    },
    {
      title: 'Choose the Correct Quantifier',
      type: 'select',
      instructions: 'Select the correct word.',
      questions: [
        {
          text: 'There is too ___ traffic today.',
          options: [
            'much',
            'many',
            'few'
          ],
          answer: 'much',
          explanation: '\'Traffic\' is uncountable → use \'much\'.'
        },
        {
          text: 'She gave me ___ useful advice.',
          options: [
            'some',
            'a few',
            'many'
          ],
          answer: 'some',
          explanation: '\'Advice\' is uncountable → use \'some\'.'
        },
        {
          text: 'How ___ students are in the class?',
          options: [
            'many',
            'much',
            'little'
          ],
          answer: 'many',
          explanation: '\'Students\' is countable → use \'many\'.'
        }
      ]
    }
  ],
  tips: [
    'Memorize common uncountable nouns: information, advice, research, equipment',
    'Use \'a piece of\' to count uncountable nouns',
    '\'Much\' for uncountable, \'many\' for countable',
    '\'A lot of\' works with both — safe choice in speaking'
  ],
  dailyPractice: [
    'Practice countable & uncountable nouns concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Countable vs Uncountable Nouns: Determines article/quantifier/verb choices. Common IELTS pitfalls include \'informations\', \'advices\', \'equipments\'.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...countableUncountableData,
      title: countableUncountableData.title + ' (Foundations)',
      overview: { ...countableUncountableData.overview, duration: '15-20 minutes' },
      sections: countableUncountableData.sections.slice(0, Math.max(2, Math.ceil(countableUncountableData.sections.length / 2))),
      exercises: countableUncountableData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...countableUncountableData,
      title: countableUncountableData.title + ' (Advanced)',
      overview: { ...countableUncountableData.overview, duration: '30-35 minutes' },
      sections: countableUncountableData.sections,
      exercises: countableUncountableData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(countableUncountableData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return countableUncountableData;
}
